import {
  BitbucketIcon,
  ConfluenceIcon,
  JiraIcon,
  OpsGenieIcon
} from '@atlaskit/logo';
import Lozenge from '@atlaskit/lozenge';
import { B400, N0, N200 } from '@atlaskit/theme/colors';
import {
  ProductKey,
  SanitizedFormattedHtmlMessage
} from '@atlassiansox/bxpkit-core';
import styled from '@emotion/styled';
import React from 'react';
import { messages } from '../../messages';

type ProductListOptions = {
  readonly products: ProductKey[];
  readonly isNew: boolean;
};

type SingleProductKeyType = {
  readonly productKey: ProductKey;
};

function RenderLogo({ productKey }: SingleProductKeyType) {
  const logoStyle = {
    iconColor: N0,
    iconGradientStart: N0,
    iconGradientStop: N0,
    size: 'large'
  };

  switch (productKey) {
    case ProductKey.JIRA_SOFTWARE:
      return <JiraIcon {...logoStyle} />;
    case ProductKey.CONFLUENCE:
      return <ConfluenceIcon {...logoStyle} />;
    case ProductKey.BITBUCKET:
      return <BitbucketIcon {...logoStyle} />;
    case ProductKey.OPSGENIE:
    case ProductKey.JIRA_SERVICE_DESK:
    case ProductKey.JIRA_SERVICE_MANAGEMENT:
      return <OpsGenieIcon {...logoStyle} />;
    default:
      return <></>;
  }
}

function RenderProductText({ productKey }: SingleProductKeyType) {
  switch (productKey) {
    case ProductKey.JIRA_SOFTWARE:
      return (
        <>
          <SanitizedFormattedHtmlMessage
            {...messages.productListJiraSoftwareName}
          />
          <SanitizedFormattedHtmlMessage
            {...messages.productListJiraSoftwareDescription}
          />
        </>
      );
    case ProductKey.CONFLUENCE:
      return (
        <>
          <SanitizedFormattedHtmlMessage
            {...messages.productListConfluenceName}
          />
          <SanitizedFormattedHtmlMessage
            {...messages.productListConfluenceDescription}
          />
        </>
      );
    case ProductKey.BITBUCKET:
      return (
        <>
          <SanitizedFormattedHtmlMessage
            {...messages.productListBitbucketName}
          />
          <SanitizedFormattedHtmlMessage
            {...messages.productListBitbucketDescription}
          />
        </>
      );
    case ProductKey.OPSGENIE:
    case ProductKey.JIRA_SERVICE_DESK:
    case ProductKey.JIRA_SERVICE_MANAGEMENT:
      return (
        <>
          <SanitizedFormattedHtmlMessage
            {...messages.productListOpsgenieName}
          />
          <SanitizedFormattedHtmlMessage
            {...messages.productListOpsgenieDescription}
          />
        </>
      );
    default:
      return <></>;
  }
}

export function ProductList({ products, isNew }: ProductListOptions) {
  return (
    <>
      {products.map((p: ProductKey) => {
        return (
          <ProductItem key={p}>
            <LogoTextWrapper>
              <LogoContainer>
                <RenderLogo productKey={p} />
              </LogoContainer>
              <ProductText>
                <RenderProductText productKey={p} />
              </ProductText>
            </LogoTextWrapper>
            {isNew && (
              <LozengeWrapper>
                <Lozenge appearance={'success'} testId={`new-${p}`}>
                  NEW
                </Lozenge>
              </LozengeWrapper>
            )}
          </ProductItem>
        );
      })}
    </>
  );
}

const ProductItem = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  position: relative;
  box-shadow: 0px 1px 1px rgba(9, 30, 66, 0.25),
    0px 0px 1px rgba(9, 30, 66, 0.31);
  border-radius: 8px;
  height: 65px;
  max-height: 65px;
  margin-bottom: 10px;
`;

const ProductText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  font-size: 16px;
  margin: 0px 0px 0px 12px;
  line-height: 18px;

  span {
    display: block;
  }

  span:last-child {
    font-size: 12px;
    color: ${N200};
  }
`;
const LogoTextWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  align-content: flex-start;
`;
const LogoContainer = styled.div`
  background: ${B400};
  height: 65px;
  width: 62px;
  min-width: 62px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px 0 0 8px;
`;
const LozengeWrapper = styled.div`
  position: absolute;
  right: 14px;
  top: 4px;
`;
